import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import ApplicationContext from "../../context/application/ApplicationContext";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import JoinApplicationScreen from "../../components/JoinApplicationScreen/JoinApplicationScreen";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../../components/GuarantorInviteSection/GuarantorInviteSection";
import TenantInviteSection, {
  TenantInvite,
} from "../../components/TenantInviteSection/TenantInviteSection";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import SkorProtectShield from "../../assets/img/skor-protect-shield.png";
import { toast } from "../../helper/common";
import { sendUniversalMail } from "../../services/sharedService";
import useTranslator from "../../hooks/useTranslator";
import styles from "./CreateApplicationStep.module.css";
import SelfManagedGuarantorsModal from "../../components/SelfManagedGuarantorsModal/SelfManagedGuarantorsModal";
import SelfManagedCoTenantsModal from "../../components/SelfManagedCoTenantsModal/SelfManagedCoTenantsModal";
import { ApplicationMember } from "./ApplicationPeopleSummary";

type CreateApplicationStepProps = {
  onBack: () => void;
  updateTenantData: any;
  onCreated?: (payload: {
    title: string;
    peopleCount: number;
    members?: ApplicationMember[];
    pendingGuarantors?: { name: string; email?: string; relation?: string }[];
    pendingCoTenants?: { name: string; email?: string }[];
  }) => void;
};

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="m4 10.5 3.4 3.4L16 5.8" />
  </svg>
);

const CreateApplicationStep = ({
  onBack,
  updateTenantData,
  onCreated,
}: CreateApplicationStepProps) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const {
    createApplication,
    consentApplication,
    pendingApplications,
    loadApplicationsData,
    applicationsLoaded,
  } = useContext(ApplicationContext);
  const { user } = useContext(AuthContext);
  const {
    createBasicGuarantorAccount,
    upsertSelectedOnboardingUsers,
    upsertSelectedCoTenants,
  } = useContext(TenantContext);
  const language = (localStorage.getItem("i18nextLng") || "en").toLowerCase();
  const defaultTitle = useMemo(
    () =>
      language.startsWith("es")
        ? `Aplicación de ${user?.fname || ""}`
        : `${user?.fname || ""}'s application`,
    [language, user?.fname]
  );
  const [title, setTitle] = useState(defaultTitle);
  const [editingTitle, setEditingTitle] = useState(false);
  const [description, setDescription] = useState(user?.about_me || "");
  const [showNote, setShowNote] = useState(true);
  const [tenantInvites, setTenantInvites] = useState<TenantInvite[]>([]);
  const [guarantorInvites, setGuarantorInvites] = useState<GuarantorInvite[]>(
    []
  );
  const [selectedOnboardingUsers, setSelectedOnboardingUsers] = useState<any[]>(
    () =>
      Array.isArray(user?.tenant_extra_field_id?.selected_onboarding_users)
        ? user.tenant_extra_field_id.selected_onboarding_users
        : []
  );
  const [selectedCoTenants, setSelectedCoTenants] = useState<any[]>(() =>
    Array.isArray(user?.tenant_extra_field_id?.selected_co_tenants)
      ? user.tenant_extra_field_id.selected_co_tenants
      : []
  );
  const [skorProtect, setSkorProtect] = useState(true);
  const [creating, setCreating] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showPendingOnRefresh, setShowPendingOnRefresh] = useState(false);
  const submittingRef = useRef(false);
  const titleChangedRef = useRef(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadApplicationsData();
    // ApplicationState recreates this function when its context updates.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!titleChangedRef.current) setTitle(defaultTitle);
  }, [defaultTitle]);

  useEffect(() => {
    const latest = [...(pendingApplications ?? [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    setSelectedApplication(latest[0] ?? null);
  }, [pendingApplications]);

  useEffect(() => {
    const savedUsers = user?.tenant_extra_field_id?.selected_onboarding_users;
    if (Array.isArray(savedUsers)) setSelectedOnboardingUsers(savedUsers);
  }, [user?._id, user?.tenant_extra_field_id?.selected_onboarding_users]);

  useEffect(() => {
    const savedCo = user?.tenant_extra_field_id?.selected_co_tenants;
    if (Array.isArray(savedCo)) setSelectedCoTenants(savedCo);
  }, [user?._id, user?.tenant_extra_field_id?.selected_co_tenants]);

  useEffect(() => {
    if (!user?.profile_completed) return;
    const selected = (
      user?.tenant_extra_field_id?.selected_onboarding_users ?? []
    ).filter((u: any) => u?.type === "guarantor");
    if (selected.length === 0) return;
    const added = user?.gurantor_added_id ?? [];
    const completedUsers: any[] =
      user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
    const normEmail = (v: any) =>
      typeof v === "string" ? v.toLowerCase() : "";
    const normName = (v: any) =>
      typeof v === "string" ? v.trim().toLowerCase() : "";
    const isCompleted = (s: any) => {
      const email = normEmail(s.email);
      if (email) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normEmail(c?.email) === email
          )
        )
          return true;
        if (
          added.some(
            (a: any) =>
              normEmail(a?.gurantor_email) === email ||
              normEmail(a?.guarantor_id?.email) === email
          )
        )
          return true;
      }
      const name = normName(s.name);
      if (name) {
        if (
          completedUsers.some(
            (c: any) => c?.type === "guarantor" && normName(c?.name) === name
          )
        )
          return true;
        if (
          added.some(
            (a: any) =>
              normName(
                `${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`.trim()
              ) === name
          )
        )
          return true;
      }
      return false;
    };
    const pending = selected.filter((s: any) => !isCompleted(s));
    if (pending.length > 0) setShowPendingOnRefresh(true);
  }, [
    user?.profile_completed,
    user?.tenant_extra_field_id?.selected_onboarding_users,
    user?.gurantor_added_id,
  ]);

  // Co-tenant pending check for self-managed
  const [showCoTenantPending, setShowCoTenantPending] = useState(false);
  useEffect(() => {
    if (!user?.profile_completed) return;
    const selected = (user?.tenant_extra_field_id?.selected_co_tenants ?? []).filter((u: any) => u?.type === "co_tenant");
    if (selected.length === 0) return;
    const completedUsers: any[] = user?.tenant_extra_field_id?.completed_co_tenants ?? [];
    const normEmail = (v: any) => typeof v === "string" ? v.toLowerCase() : "";
    const normName = (v: any) => typeof v === "string" ? v.trim().toLowerCase() : "";
    const isCompleted = (s: any) => {
      const email = normEmail(s.email);
      if (email && completedUsers.some((c: any) => normEmail(c?.email) === email)) return true;
      const name = normName(s.name);
      if (name && completedUsers.some((c: any) => normName(c?.name) === name)) return true;
      return false;
    };
    const pending = selected.filter((s: any) => !isCompleted(s));
    if (pending.length > 0) setShowCoTenantPending(true);
  }, [user?.profile_completed, user?.tenant_extra_field_id?.selected_co_tenants, user?.tenant_extra_field_id?.completed_co_tenants]);

  const existingGuarantors = user?.gurantor_added_id ?? [];
  const peopleCount =
    1 +
    tenantInvites.length +
    guarantorInvites.length +
    existingGuarantors.length;

  const submitGuarantors = async (invitesToSubmit: GuarantorInvite[]) => {
    const emailInvites = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod !== "self"
    );
    const selfManagedGuarantors = invitesToSubmit.filter(
      ({ guarantorMethod }) => guarantorMethod === "self"
    );

    for (const guarantor of emailInvites) {
      const [fname, ...surname] = guarantor.name.split(/\s+/);
      try {
        await createBasicGuarantorAccount({
          fname,
          lname: surname.join(" "),
          email: guarantor.email,
          gurantor_relation: guarantor.relation,
          step: 1,
        });
      } catch (err: any) {
        if (err?.response?.data?.errorKey !== "account-already-present") {
          throw err;
        }
      }

      await sendUniversalMail({
        receiverEmail: guarantor.email,
        senderId: localStorage.getItem("saveduserid"),
        emailType: "score",
        status: "processing",
        gurantor_relation: guarantor.relation,
      });
    }

    if (selfManagedGuarantors.length > 0) {
      const newSelectedUsers = selfManagedGuarantors
        .map(({ name, email, relation }) => ({
          name,
          type: "guarantor",
          email,
          gurantor_relation: relation,
        }))
        .filter(
          ({ name, type }) =>
            !selectedOnboardingUsers.some(
              (savedUser) =>
                savedUser?.type === type && savedUser?.name === name
            )
        );
      const nextSelectedUsers = [
        ...selectedOnboardingUsers,
        ...newSelectedUsers,
      ];
      const result = await upsertSelectedOnboardingUsers({
        tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
        users: nextSelectedUsers,
      });
      if (result === null) throw new Error("failed-to-add-guarantor");
      setSelectedOnboardingUsers(nextSelectedUsers);
    }
  };

  const submitCoTenants = async (invitesToSubmit: TenantInvite[]) => {
    const selfManaged = invitesToSubmit.filter(({ tenantMethod }) => tenantMethod === "self");
    if (selfManaged.length === 0) return;
    const newSelected = selfManaged
      .map(({ name, email }) => ({ name, type: "co_tenant", email }))
      .filter(
        ({ name, type }) =>
          !selectedCoTenants.some((saved: any) => saved?.type === type && saved?.name === name)
      );
    const next = [...selectedCoTenants, ...newSelected];
    const result = await upsertSelectedCoTenants({
      tenant_id: user?._id ?? localStorage.getItem("saveduserid"),
      users: next,
    });
    if (result === null) throw new Error("failed-to-add-co-tenant");
    setSelectedCoTenants(next);
  };

  const handleCreate = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setCreating(true);

    try {
      const isIndividually = tenantInvites.length === 0;
      const tenantUpdatePayload: any = {
        about_me: description,
        skor_protect: skorProtect,
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      };
      if (isIndividually) {
        tenantUpdatePayload.application_title = title.trim() || defaultTitle;
      }
      await submitGuarantors(guarantorInvites);
      await submitCoTenants(tenantInvites);
      if (tenantInvites.length > 0) {
        await createApplication({
          participants: tenantInvites.map(
            ({ name: inviteName, email: inviteEmail, tenantMethod }) => ({
              name: inviteName,
              email: inviteEmail,
              selfManaged: tenantMethod === "self",
            })
          ),
          title: title.trim() || defaultTitle,
          skor_protect: skorProtect,
          description: description.trim(),
        });
      }
      const createdTitle = title.trim() || defaultTitle;
      const pendingGuarantors = guarantorInvites
        .filter((g) => g.guarantorMethod === "self")
        .map((g) => ({
          name: g.name,
          email: g.email,
          relation: g.relation,
        }));
      // also include previously selected self-managed already stored
      const existingPending = selectedOnboardingUsers
        .filter((u: any) => u?.type === "guarantor")
        .map((u: any) => ({
          name: u.name,
          email: u.email,
          relation: u.gurantor_relation ?? u.relation,
        }));
      const allPending = [
        ...existingPending,
        ...pendingGuarantors.filter(
          (p) =>
            !existingPending.some((e) =>
              e.email
                ? e.email.toLowerCase() === p.email.toLowerCase()
                : e.name?.trim().toLowerCase() === p.name.trim().toLowerCase()
            )
        ),
      ];
      const pendingCoTenants = tenantInvites
        .filter((t) => t.tenantMethod === "self")
        .map((t) => ({ name: t.name, email: t.email }));
      const existingCoPending = selectedCoTenants
        .filter((u: any) => u?.type === "co_tenant")
        .map((u: any) => ({ name: u.name, email: u.email }));
      const allCoPending = [
        ...existingCoPending,
        ...pendingCoTenants.filter(
          (p) =>
            !existingCoPending.some((e) =>
              e.email
                ? e.email.toLowerCase() === p.email.toLowerCase()
                : e.name?.trim().toLowerCase() === p.name.trim().toLowerCase()
            )
        ),
      ];
      const applicationMembers: ApplicationMember[] = [
        {
          name:
            `${user?.fname ?? ""} ${user?.lname ?? ""}`.trim() ||
            user?.email ||
            _t("you", "You"),
          email: user?.email,
          role: "tenant",
        },
        ...tenantInvites.map(({ name, email }) => ({
          name,
          email,
          role: "tenant" as const,
        })),
        ...guarantorInvites.map(({ name, email, guarantorMethod }) => ({
          name,
          email,
          role: "guarantor" as const,
          status:
            guarantorMethod === "self"
              ? ("pendingProfile" as const)
              : ("invitationSent" as const),
        })),
        ...existingGuarantors.map((guarantor: any) => ({
          name:
            guarantor?.gurantor_name ||
            `${guarantor?.guarantor_id?.fname ?? ""} ${guarantor?.guarantor_id?.lname ?? ""
              }`.trim() ||
            guarantor?.gurantor_email ||
            _t("guarantor", "Guarantor"),
          email:
            guarantor?.gurantor_email || guarantor?.guarantor_id?.email,
          role: "guarantor" as const,
        })),
      ];
      await updateTenantData(tenantUpdatePayload);
      if (onCreated)
        onCreated({
          title: createdTitle,
          peopleCount,
          members: applicationMembers,
          pendingGuarantors: allPending,
          pendingCoTenants: allCoPending,
        });
      else navigate("/home?welcome=true");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
        _t("failedToCreateApplication", "Failed to create application")
      );
    } finally {
      submittingRef.current = false;
      setCreating(false);
    }
  };

  const handleAcceptInvitation = async (
    joinGuarantorInvites: GuarantorInvite[]
  ) => {
    if (!selectedApplication) return;
    setProcessingInvitation(true);
    try {
      await submitGuarantors(joinGuarantorInvites);
      await consentApplication(selectedApplication._id, "accepted");
      await updateTenantData({
        is_edit: false,
        profile_completed: true,
        role: localStorage.getItem("usertype"),
      });
      const joinedTitle = selectedApplication?.title || defaultTitle;
      if (onCreated) onCreated({ title: joinedTitle, peopleCount });
      else navigate("/home?welcome=true");
    } catch {
      toast.error(_t("failedToJoinGroup", "Failed to join application"));
    } finally {
      setProcessingInvitation(false);
    }
  };

  if (!applicationsLoaded) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <img src={SkorDefaultLoader} className={styles.loader} alt="" />
      </div>
    );
  }

  if (selectedApplication) {
    return (
      <div className={`${styles.overlay} modal-fixed-screen`}>
        <JoinApplicationScreen
          app={selectedApplication}
          onAccept={handleAcceptInvitation}
          onBack={onBack}
          processing={processingInvitation}
          user={user}
          _t={_t}
        />
      </div>
    );
  }

  if (showCoTenantPending || showPendingOnRefresh) {
    // Build co-tenant pending
    const buildCoPending = () => {
      const selectedRaw = selectedCoTenants.filter((u: any) => u?.type === "co_tenant") as any[];
      const completedUsers: any[] = user?.tenant_extra_field_id?.completed_co_tenants ?? [];
      const normEmail = (v: any) => typeof v === "string" ? v.toLowerCase() : "";
      const normName = (v: any) => typeof v === "string" ? v.trim().toLowerCase() : "";
      const keyOf = (g: any) => normEmail(g.email) || normName(g.name);
      const mergedMap = new Map<string, any>();
      for (const g of selectedRaw) { const k = keyOf(g); if (k && !mergedMap.has(k)) mergedMap.set(k, g); }
      for (const c of completedUsers) { const k = keyOf(c); if (k && !mergedMap.has(k)) mergedMap.set(k, { name: c.name, email: c.email, type: "co_tenant" }); }
      const selected = Array.from(mergedMap.values());
      return selected.map((p: any) => {
        const email = normEmail(p.email);
        const name = normName(p.name);
        let completed = false;
        if (email && completedUsers.some((c: any) => normEmail(c?.email) === email)) completed = true;
        if (!completed && name && completedUsers.some((c: any) => normName(c?.name) === name)) completed = true;
        return { ...p, completed, type: "co_tenant" };
      });
    };
    const buildGuarPending = () => {
      const selectedRaw = selectedOnboardingUsers.filter((u: any) => u?.type === "guarantor") as any[];
      const added = user?.gurantor_added_id ?? [];
      const completedUsers: any[] = user?.tenant_extra_field_id?.completed_onboarding_users ?? [];
      const normEmail = (v: any) => typeof v === "string" ? v.toLowerCase() : "";
      const normName = (v: any) => typeof v === "string" ? v.trim().toLowerCase() : "";
      const keyOf = (g: any) => normEmail(g.email) || normName(g.name);
      const mergedMap = new Map<string, any>();
      for (const g of selectedRaw) { const k = keyOf(g); if (k && !mergedMap.has(k)) mergedMap.set(k, g); }
      for (const c of completedUsers.filter((c: any) => c?.type === "guarantor")) { const k = keyOf(c); if (k && !mergedMap.has(k)) mergedMap.set(k, { name: c.name, email: c.email, type: "guarantor" }); }
      const selected = Array.from(mergedMap.values());
      return selected.map((p: any) => {
        const email = normEmail(p.email);
        const name = normName(p.name);
        let completed = false;
        if (email) {
          if (completedUsers.some((c: any) => c?.type === "guarantor" && normEmail(c?.email) === email)) completed = true;
          else if (added.some((a: any) => normEmail(a?.gurantor_email) === email || normEmail(a?.guarantor_id?.email) === email)) completed = true;
        }
        if (!completed && name) {
          if (completedUsers.some((c: any) => c?.type === "guarantor" && normName(c?.name) === name)) completed = true;
          else if (added.some((a: any) => { const an = normName(`${a?.guarantor_id?.fname ?? ""} ${a?.guarantor_id?.lname ?? ""}`.trim()); return !!an && an === name; })) completed = true;
        }
        return { ...p, completed, type: "guarantor" };
      });
    };
    const coPending = buildCoPending();
    const guarPending = buildGuarPending();
    const combined = [...guarPending, ...coPending];
    const hasAnyPending = combined.some((x) => !x.completed);
    if (!hasAnyPending && combined.length === 0) {
      // nothing to show, clear flags
    } else if (combined.length > 0) {
      return (
        <div className={`${styles.overlay} modal-fixed-screen`}>
          <SelfManagedGuarantorsModal
            guarantors={combined as any}
            onLater={() => {
              setShowPendingOnRefresh(false);
              setShowCoTenantPending(false);
            }}
            inline
          />
        </div>
      );
    }
  }

  return (
    <div className={`${styles.overlay} modal-fixed-screen`}>
      <main className={`${styles.card} onboarding-modal-surface`}>
        <button className={styles.backButton} type="button" onClick={onBack}>
          <span aria-hidden="true">‹</span> {_t("back", "Back")}
        </button>

        <h1>{_t("createYourApplication", "Create your application")}</h1>

        <label className={styles.titleLabel} htmlFor="application-title">
          {_t("applicationName", "Application name")}
        </label>
        <div className={styles.titleField}>
          {editingTitle ? (
            <input
              id="application-title"
              ref={titleInputRef}
              value={title}
              maxLength={80}
              onChange={(event) => {
                titleChangedRef.current = true;
                setTitle(event.target.value);
              }}
              onBlur={() => {
                setTitle((current) => {
                  if (current.trim()) return current.trim();
                  titleChangedRef.current = false;
                  return defaultTitle;
                });
                setEditingTitle(false);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
              }}
            />
          ) : (
            <span>{title}</span>
          )}
          <button
            type="button"
            onClick={() => {
              setEditingTitle(true);
              requestAnimationFrame(() => titleInputRef.current?.focus());
            }}
            aria-label={_t("editApplicationName", "Edit application name")}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m4 20 4.2-1 10.4-10.4a2.4 2.4 0 0 0-3.4-3.4L4.8 15.6 4 20Zm9.8-13.4 3.4 3.4" />
            </svg>
          </button>
        </div>

        <p className={styles.intro}>
          {_t(
            "addPeopleToRentalApplication",
            "Add the people who'll be part of your rental. Each one gets an email invitation, or you can fill in their profile."
          )}
        </p>

        <section className={styles.people} aria-label={_t("people", "People")}>
          <div className={styles.personRow}>
            <span className={styles.avatar}>
              {(user?.fname || user?.email || "Y").charAt(0).toUpperCase()}
            </span>
            <div>
              <div className={styles.personName}>
                <strong>{_t("you", "You")}</strong>
                <span>{_t("leadTenant", "Lead tenant")}</span>
              </div>
              <p>{user?.email}</p>
            </div>
          </div>

          {(
            selectedOnboardingUsers.filter(
              (u: any) => u?.type === "guarantor"
            ) as any[]
          ).map((g: any) => (
            <div
              className={`${styles.addedPerson} ${styles.addedPersonIndented}`}
              key={g.email || g.name}
            >
              <span className={styles.avatar}>
                {(g.name || g.email || "G").charAt(0).toUpperCase()}
              </span>
              <div>
                <strong>{g.name}</strong>
                <p>
                  {g.email} · {_t("guarantor", "Guarantor")}
                </p>
              </div>
            </div>
          ))}
          <GuarantorInviteSection
            currentUserEmail={user?.email}
            invites={guarantorInvites}
            onChange={setGuarantorInvites}
            _t={_t}
          />

          <div className={styles.tenantDivider} />

          {(selectedCoTenants.filter((u: any) => u?.type === "co_tenant") as any[]).map((g: any) => (
            <div className={styles.addedPerson} key={g.email || g.name}>
              <span className={styles.avatar}>{(g.name || g.email || "G").charAt(0).toUpperCase()}</span>
              <div>
                <strong>{g.name}</strong>
                <p>
                  {g.email} · {_t("co_tenant", "Co-tenant")}
                </p>
              </div>
            </div>
          ))}
          <TenantInviteSection
            currentUserEmail={user?.email}
            invites={tenantInvites}
            onChange={setTenantInvites}
            _t={_t}
          />
        </section>

        <div className={styles.noteSection}>
          <div className={styles.noteHeader}>
            <span>
              {_t(
                "addANoteAboutHousehold",
                "Add a note about your household (optional)"
              )}
            </span>
            <button
              type="button"
              className={`${styles.noteToggle} ${showNote ? styles.noteToggleOpen : ""}`}
              onClick={() => setShowNote((current) => !current)}
              aria-expanded={showNote}
              aria-controls="application-household-note"
              aria-label={
                showNote
                  ? _t("collapse", "Collapse")
                  : _t("expand", "Expand")
              }
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="m5 7.5 5 5 5-5" />
              </svg>
            </button>
          </div>
          {showNote && (
            <textarea
              id="application-household-note"
              className={styles.description}
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={_t(
                "applicationDescriptionExample",
                "E.g. We're a couple with permanent contracts, relocating for work in March."
              )}
              aria-label={_t(
                "applicationDescription",
                "Application description"
              )}
            />
          )}
        </div>

        <label
          className={`${styles.protectCard} ${skorProtect ? styles.protectCardActive : ""}`}
        >
          <input
            type="checkbox"
            checked={skorProtect}
            onChange={(event) => setSkorProtect(event.target.checked)}
          />
          <span className={styles.customCheckbox} aria-hidden="true">
            {skorProtect && <CheckIcon />}
          </span>
          <div className={styles.protectContent}>
            <div className={styles.protectHeading}>
              <span className={styles.shield}>
                <img src={SkorProtectShield} alt="" />
              </span>
              <strong>{_t("skorProtectTitle", "Skor Protect")}</strong>
              <span className={styles.badge}>
                {_t("recommended", "Recommended")}
              </span>
            </div>
            <p>
              {_t(
                "skorProtectDesc",
                "Signal to landlords that rent is backed by Skor, boosting your chances of getting the property."
              )}
            </p>
            <ul>
              <li>
                <CheckIcon />
                <span>
                  <strong>
                    {_t("skorBenefit1Bold", "Landlords trust you more")}
                  </strong>{" "}
                  - {_t("skorBenefit1Rest", "rent is backed")}
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>{_t("skorBenefit2Bold", "You're covered")}</strong> -{" "}
                  {_t(
                    "skorBenefit2Rest",
                    "if a dispute with your landlord arises we help you"
                  )}
                </span>
              </li>
              <li>
                <CheckIcon />
                <span>
                  <strong>
                    {_t("skorBenefit3Bold", "Stronger candidacy")}
                  </strong>{" "}
                  - {_t("skorBenefit3Rest", "visible on your shared profile")}
                </span>
              </li>
            </ul>
            <p className={styles.pricing}>
              {_t("skorNoChargeNow", "No charge now")} ·{" "}
              <u>
                {_t(
                  "skorPriceConfirmed",
                  "Price confirmed once the rental amount is set"
                )}
              </u>
            </p>
          </div>
        </label>

        <footer className={styles.footer}>
          <p>
            {peopleCount}{" "}
            {peopleCount === 1
              ? _t("person", "person")
              : _t("people", "people")}{" "}
            {_t("inThisApplication", "in this application")}.
          </p>
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            aria-busy={creating}
          >
            {creating && (
              <span className={styles.buttonLoader} aria-hidden="true" />
            )}
            <span>
              {creating
                ? `${_t("creatingApplication", "Creating application")}...`
                : _t("createApplication", "Create application")}
            </span>
          </button>
        </footer>
      </main>
    </div>
  );
};

export default CreateApplicationStep;
